import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import capitalizeFilterLabel from "../utils/capitalizeFilterLabel";

function useDebouncedValue(value, delayMs = 150) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

function normalizeOptions(options) {
  if (!Array.isArray(options)) return [];
  const seen = new Set();
  return options
    .map((opt) => {
      if (opt == null) return null;
      if (typeof opt === "object" && opt.value != null) {
        const value = String(opt.value);
        if (!value || seen.has(value)) return null;
        seen.add(value);
        return {
          value,
          label: opt.label != null ? String(opt.label) : capitalizeFilterLabel(value),
        };
      }
      const value = String(opt).trim();
      if (!value || seen.has(value)) return null;
      seen.add(value);
      return { value, label: capitalizeFilterLabel(value) };
    })
    .filter(Boolean);
}

/** 0 = show all options (no UI cap) */
const DEFAULT_MAX_VISIBLE = 0;

export default function FilterSearchableSelect({
  id,
  value = "",
  onChange,
  options = [],
  placeholder = "Search…",
  disabled = false,
  loading = false,
  selectedLabel: selectedLabelProp = "",
  onQueryChange,
  maxVisibleOptions = DEFAULT_MAX_VISIBLE,
  minQueryLength = 0,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const debouncedQuery = useDebouncedValue(query);

  const normalizedOptions = useMemo(() => normalizeOptions(options), [options]);
  const selectedValue = value ? String(value) : "";

  const selectedLabel = useMemo(() => {
    if (!selectedValue) return "";
    const match = normalizedOptions.find((o) => o.value === selectedValue);
    return match?.label || selectedLabelProp || selectedValue;
  }, [normalizedOptions, selectedValue, selectedLabelProp]);

  const filteredOptions = useMemo(() => {
    const q = debouncedQuery.trim().toLowerCase();
    if (minQueryLength > 0 && q.length < minQueryLength) {
      return [];
    }
    if (!q) {
      return maxVisibleOptions > 0 && normalizedOptions.length > maxVisibleOptions
        ? normalizedOptions.slice(0, maxVisibleOptions)
        : normalizedOptions;
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    const matched = normalizedOptions.filter((opt) => {
      const val = opt.value.toLowerCase();
      const lbl = opt.label.toLowerCase();
      if (val.includes(q) || lbl.includes(q)) return true;
      return tokens.length > 0 && tokens.every((t) => val.includes(t) || lbl.includes(t));
    });
    if (maxVisibleOptions > 0 && matched.length > maxVisibleOptions) {
      return matched.slice(0, maxVisibleOptions);
    }
    return matched;
  }, [normalizedOptions, debouncedQuery, maxVisibleOptions, minQueryLength]);

  const showTypeToSearchHint =
    minQueryLength > 0 && debouncedQuery.trim().length < minQueryLength;
  const showResultCapHint =
    maxVisibleOptions > 0 &&
    !showTypeToSearchHint &&
    !debouncedQuery.trim() &&
    normalizedOptions.length > maxVisibleOptions;

  useEffect(() => {
    if (!open) {
      setQuery("");
      return undefined;
    }
    const t = setTimeout(() => searchRef.current?.focus(), 50);
    const onPointerDown = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open]);

  const select = (val) => {
    onChange(val);
    setOpen(false);
    setQuery("");
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange("");
    setOpen(false);
    setQuery("");
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        id={`${id}-control`}
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => {
          if (disabled) return;
          setOpen((v) => !v);
        }}
        className={[
          "flex h-8 w-full items-center gap-1 rounded border bg-white px-2 text-left text-xs transition-colors",
          disabled
            ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
            : open
              ? "border-gray-500 ring-1 ring-gray-400"
              : "border-gray-300 hover:border-gray-400",
        ].join(" ")}
      >
        <span
          className={[
            "min-w-0 flex-1 truncate",
            selectedValue ? "text-gray-900" : "text-gray-600",
          ].join(" ")}
          title={selectedLabel || undefined}
        >
          {selectedValue ? selectedLabel : placeholder}
        </span>
        {selectedValue ? (
          <span
            role="button"
            tabIndex={0}
            onClick={clear}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") clear(e);
            }}
            className="shrink-0 text-[10px] text-gray-400 hover:text-red-600"
            aria-label="Clear selection"
          >
            ×
          </span>
        ) : null}
        <ChevronDown
          className={`h-3.5 w-3.5 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 z-[120] mt-1 flex max-h-56 flex-col overflow-hidden rounded border border-gray-200 bg-white shadow-lg">
          <div className="border-b border-gray-100 bg-gray-50 p-1.5">
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                onQueryChange?.(e.target.value);
              }}
              placeholder={placeholder}
              className="w-full rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 focus:border-gray-500 focus:outline-none focus:ring-1 focus:ring-gray-400"
            />
          </div>
          <ul className="min-h-[2rem] overflow-y-auto py-0.5" role="listbox">
            {loading ? (
              <li className="px-2 py-4 text-center text-[10px] text-gray-500">Loading…</li>
            ) : filteredOptions.length > 0 ? (
              filteredOptions.map((opt) => {
                const checked = selectedValue === opt.value;
                return (
                  <li key={opt.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={checked}
                      onClick={() => select(opt.value)}
                      className={[
                        "flex w-full items-center px-2 py-1.5 text-left text-xs transition-colors hover:bg-gray-50",
                        checked ? "bg-gray-100 font-medium text-gray-900" : "text-gray-700",
                      ].join(" ")}
                    >
                      <span className="truncate">{opt.label}</span>
                    </button>
                  </li>
                );
              })
            ) : (
              <li className="px-2 py-4 text-center text-[10px] italic text-gray-400">
                {showTypeToSearchHint
                  ? `Type at least ${minQueryLength} character${minQueryLength === 1 ? "" : "s"} to search`
                  : normalizedOptions.length > 0
                    ? "No matches"
                    : "No options"}
              </li>
            )}
            {showResultCapHint ? (
              <li className="border-t border-gray-100 px-2 py-1.5 text-center text-[10px] text-gray-500">
                Showing first {maxVisibleOptions}. Type to narrow results.
              </li>
            ) : null}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
